import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
 ExternalLink,
 BookOpen,
 FileText,
 Wrench,
 Sparkles,
 User,
 Zap,
 Gem,
 ArrowRight
} from 'lucide-react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import LoginModal from './LoginModal';

// AI工具配置 - 参考AI Fleet的设计风格
const AI_TOOLS = [
 {
 id: 'google-scholar',
 name: 'Google Scholar',
 nameZh: '谷歌学术',
 description: '搜索学术文献、论文、引文、书籍、会议论文、学位论文等学术资源',
 url: 'https://scholar.google.com',
 internalRoute: '/google-scholar/sidebar',
 icon: BookOpen,
 gradient: 'from-accent-500 to-accent-600',
 image: '/images/google-scholar.jpg',
 costKind: 'free'
 },
 {
 id: 'aippt',
 name: 'AI-PPT',
 nameZh: 'AI-PPT',
 description: '使用AI技术快速生成专业演示文稿，支持多种模板和自动排版',
 url: 'https://www.aippt.cn',
 internalRoute: '/ai-toolbox/sidebar/aippt',
 icon: FileText,
 gradient: 'from-accent-500 to-accent-600',
 image: '/images/aippt-placeholder.svg',
 statusLabel: '内测中',
 costKind: 'diamond',
 costAmount: 99
 }
];

export default function AIToolboxPage() {
 const { t, i18n } = useTranslation();
 const navigate = useNavigate();
 const { isLoggedIn, showLoginModal, requireAuth, closeLoginModal, handleLoginSuccess } = useAuthGuard();
 const [selectedTool, setSelectedTool] = useState(null);

 // 处理工具点击
 const handleToolClick = (tool) => {
 if (tool.disabled) {
 return;
 }

 if (!requireAuth()) {
 return;
 }

 setSelectedTool(tool);

 if (tool.internalRoute) {
 navigate(tool.internalRoute);
 } else {
 window.open(tool.url, '_blank', 'noopener,noreferrer');
 }

 console.log(`用户访问了工具: ${tool.name}`);
 };

 return (
 <>
 <div className="min-h-screen bg-cream-50">
 <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
 
 {/* 页面标题 - 简洁版 */}
 <div className="text-center mb-6 sm:mb-8">
 <div className="flex items-center justify-center gap-3 mb-3">
 <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent-100 rounded-lg flex items-center justify-center">
 <Wrench className="w-5 h-5 sm:w-6 sm:h-6 text-accent-600" />
 </div>
 <h1 className="text-2xl sm:text-3xl font-bold text-ink-900">{t('sidebar.aiFeatures.aiToolbox')}</h1>
 </div>
 <p className="text-base sm:text-lg text-ink-600 max-w-2xl mx-auto">
 {t('aiToolbox.description', '发现和使用各种AI工具，提升工作效率，激发创意潜能')}
 </p>
 </div>

 {/* AI工具网格 - 对齐 /ai-fleet 顶部车队卡片：朴素 cream 底、ink 细边、激活紫环 */}
 <div className="flex justify-center">
 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 lg:gap-6 mb-6 sm:mb-8 w-full max-w-3xl">
 {AI_TOOLS.map((tool) => {
 const IconComponent = tool.icon;
 const isSelected = selectedTool && selectedTool.id === tool.id;
 const toolName = i18n.language.startsWith('zh') ? tool.nameZh : tool.name;

 return (
 <div
 key={tool.id}
 className={`bg-cream-50 rounded-2xl overflow-hidden border transition-all duration-200 group ${
 tool.disabled
 ? 'opacity-75 cursor-not-allowed border-ink-200'
 : 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md'
 } ${
 isSelected
 ? 'border-accent-500 ring-1 ring-accent-500 shadow-md'
 : 'border-ink-200 hover:border-accent-300'
 }`}
 onClick={() => !tool.disabled && handleToolClick(tool)}
 >
 {/* 工具图标区域 - 正方形，微渐变底 */}
 <div className={`aspect-square relative overflow-hidden transition-colors duration-300 ${
 isSelected
 ? 'bg-gradient-to-br from-accent-50 to-accent-100/60'
 : 'bg-gradient-to-br from-cream-100 to-accent-50/30 group-hover:to-accent-50/60'
 }`}>
 <div className="relative h-full flex items-center justify-center p-3 sm:p-4">
 {/* 主工具图片容器 */}
 <div className="relative z-10 w-3/4 h-3/4 transition-transform duration-300 group-hover:scale-105">
 <div className={`w-full h-full rounded-xl overflow-hidden border transition-colors duration-200 bg-cream-50 ${
 isSelected ? 'border-accent-300 shadow-sm' : 'border-ink-100 group-hover:border-accent-200'
 }`}>
 <img
 src={tool.image}
 alt={toolName}
 className="w-full h-full object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 {/* 备用图标显示 */}
 <div
 className={`w-full h-full bg-gradient-to-br ${tool.gradient} rounded-xl flex items-center justify-center`}
 style={{display: 'none'}}
 >
 <IconComponent className="w-12 h-12 text-white" />
 </div>
 </div>
 </div>

 {/* 仅开发中状态徽章 —— 免费/付费已下移至信息区 pill 徽章，去掉内测中 */}
 {tool.disabled && (
 <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold z-20 bg-cream-100 text-ink-600 border border-ink-200">
 <span>开发中</span>
 </div>
 )}

 {/* 选中标记 */}
 {isSelected && (
 <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-accent-500 flex items-center justify-center z-20 shadow-sm">
 <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
 </div>
 )}
 </div>
 </div>

 {/* 工具信息 */}
 <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-2 space-y-2">
 {/* 名称 */}
 <h3 className={`text-sm sm:text-base font-bold leading-tight text-center transition-colors duration-200 ${
 isSelected ? 'text-accent-600' : 'text-ink-900 group-hover:text-accent-600'
 }`}>
 {toolName}
 </h3>

 {/* 费用徽章 —— pill 样式 */}
 <div className="flex items-center justify-center">
 {tool.costKind === 'free' && (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-700">
 <span className="w-1 h-1 rounded-full bg-green-500"></span>
 {t('aiToolbox.cost.free', '免费')}
 </span>
 )}
 {tool.costKind === 'diamond' && (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-accent-100 text-accent-700">
 <Gem className="w-3 h-3" />
 {t('aiToolbox.cost.perUse', '{{amount}} 钻/次').replace('{{amount}}', tool.costAmount)}
 </span>
 )}
 </div>
 </div>

 {/* CTA 栏 - 常驻入口，告诉用户这是可点的功能卡 */}
 <div className={`px-3 sm:px-4 py-2.5 border-t flex items-center justify-center gap-1.5 text-xs sm:text-sm font-semibold transition-colors duration-200 ${
 tool.disabled
 ? 'bg-cream-100 border-ink-200 text-ink-400'
 : isSelected
 ? 'bg-accent-500 border-accent-500 text-white'
 : 'bg-cream-100 border-ink-200 text-ink-700 group-hover:bg-accent-500 group-hover:border-accent-500 group-hover:text-white'
 }`}>
 <span>{tool.disabled ? '即将上线' : t('aiToolbox.useNow', '立即使用')}</span>
 {!tool.disabled && (
 <ArrowRight className={`w-3.5 h-3.5 transition-transform duration-200 ${
 isSelected ? 'translate-x-0' : 'group-hover:translate-x-0.5'
 }`} />
 )}
 </div>
 </div>
 );
 })}
 </div>
 </div>

 {/* 已选工具详情 */}
 {selectedTool && (
 <div className="bg-accent-50/60 border-l-4 border-accent-500 rounded-r-xl mb-6 sm:mb-8">
 <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
 <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
 {/* 左侧：工具详细信息 */}
 <div className="flex items-center gap-3 sm:gap-4">
 {/* 工具图标 */}
 <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl overflow-hidden shadow-sm bg-cream-50 p-1 border border-ink-200">
 <img 
 src={selectedTool.image}
 alt={selectedTool.name}
 className="w-full h-full rounded-lg object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 <div 
 className={`w-full h-full bg-gradient-to-br ${selectedTool.gradient} rounded-lg flex items-center justify-center`}
 style={{display: 'none'}}
 >
 <selectedTool.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
 </div>
 </div>
 
 {/* 工具信息 */}
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-1">
 <h3 className="text-lg sm:text-xl font-bold text-ink-900">
 {i18n.language.startsWith('zh') ? selectedTool.nameZh : selectedTool.name}
 </h3>
 <ExternalLink className="w-4 h-4 text-ink-500" />
 </div>
 <p className="text-sm sm:text-base text-ink-600 leading-relaxed">
 {selectedTool.description}
 </p>
 {/* 费用说明（详情区） */}
 <div className="mt-2 flex items-center gap-2">
 {selectedTool.costKind === 'free' && (
 <>
 <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">{t('aiToolbox.cost.free', '免费')}</span>
 <span className="text-xs text-ink-500">{t('aiToolbox.cost.freeHint', '站点用户可免费使用')}</span>
 </>
 )}
 {selectedTool.costKind === 'diamond' && (
 <>
 <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium inline-flex items-center gap-1">
 <Gem className="w-3.5 h-3.5" />
 {t('aiToolbox.cost.perUse', '{{amount}} 钻/次').replace('{{amount}}', selectedTool.costAmount)}
 </span>
 <span className="text-xs text-ink-500">{t('aiToolbox.cost.aipptCostHint', '每次使用消耗99个钻石').replace('{{amount}}', selectedTool.costAmount)}</span>
 </>
 )}
 </div>
 </div>
 </div>

 {/* 右侧：访问按钮 */}
 <div className="flex gap-3">
 <button
 onClick={() => !selectedTool.disabled && handleToolClick(selectedTool)}
 disabled={selectedTool.disabled}
 className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors duration-300 flex items-center gap-2 ${
 selectedTool.disabled
 ? 'bg-ink-300 text-white cursor-not-allowed'
 : 'bg-accent-500 text-white hover:bg-accent-600'
 }`}
 >
 <span>{selectedTool.disabled ? selectedTool.disabledText : t('common.visit', '访问工具')}</span>
 {!selectedTool.disabled && <ExternalLink className="w-4 h-4" />}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* 底部提示信息 - 简化版 */}
 <div className="text-center">
 <div className="inline-flex items-center gap-2 px-4 py-2 bg-cream-50 border border-ink-200 rounded-full text-sm text-ink-600">
 <Sparkles className="w-4 h-4 text-accent-500" />
 <span>{t('aiToolbox.stats.totalTools', { count: AI_TOOLS.length }, `共 ${AI_TOOLS.length} 个工具`)}</span>
 </div>
 </div>
 </div>
 </div>

 <LoginModal
 isOpen={showLoginModal}
 onClose={closeLoginModal}
 onLogin={handleLoginSuccess}
 />
 </>
 );
}
